import React, { useEffect, useState } from 'react';
import { ChevronDown, Lock, X } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { Tag } from '../TagInput/Tag';
import { useReportSettings } from '../../contexts/ReportSettingsContext';
import type { MonthlyClubReportSettings, ReportSectionKey } from '../../contexts/ReportSettingsContext';
import { optionalReportSections, reportSendTimes } from '../../data/monthlyClubReportSections';
interface ReportSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function Switch({
  checked,
  label,
  onChange,
  disabled = false
}: {
  checked: boolean;
  label: string;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
}) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange?.(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 disabled:cursor-not-allowed ${checked ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'} ${disabled ? 'opacity-60' : ''}`} data-id="element-3646" data-mp="13ti7fzybcglp">
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-3647" data-mp="39dwgpzp7p4n0" />
    </button>;
}
export function ReportSettingsDrawer({
  isOpen,
  onClose,
  onSaved
}: ReportSettingsDrawerProps) {
  const {
    settings,
    saveSettings
  } = useReportSettings();
  const [draft, setDraft] = useState<MonthlyClubReportSettings>(settings);
  const [newRecipient, setNewRecipient] = useState('');
  const [recipientError, setRecipientError] = useState('');

  // Start from the saved settings each time the drawer opens
  useEffect(() => {
    if (isOpen) {
      setDraft({
        ...settings,
        recipients: [...settings.recipients],
        sections: {
          ...settings.sections
        }
      });
      setNewRecipient('');
      setRecipientError('');
    }
  }, [isOpen, settings]);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const addRecipient = () => {
    const email = newRecipient.trim();
    if (!email) return;
    if (!EMAIL_PATTERN.test(email)) {
      setRecipientError('Please enter a valid email address');
      return;
    }
    if (draft.recipients.includes(email)) {
      setRecipientError('Email already added');
      return;
    }
    setDraft(prev => ({
      ...prev,
      recipients: [...prev.recipients, email]
    }));
    setNewRecipient('');
    setRecipientError('');
  };
  const toggleSection = (key: ReportSectionKey, on: boolean) => setDraft(prev => ({
    ...prev,
    sections: {
      ...prev.sections,
      [key]: on
    }
  }));
  const handleSave = () => {
    saveSettings(draft);
    onSaved();
    onClose();
  };
  return <div className="fixed inset-0 z-[1050] flex justify-end" data-id="element-3648" data-mp="v76ee3l9djvu">
      <div className="absolute inset-0 bg-[#0F172A]/50 backdrop-blur-sm" onClick={onClose} data-id="element-3649" data-mp="ghqpyu979qjb" />
      <aside role="dialog" aria-modal="true" aria-labelledby="report-settings-title" className="relative w-full max-w-[480px] h-full bg-white shadow-[0_20px_25px_rgba(15,23,42,0.15)] flex flex-col" data-id="element-3650" data-mp="lgbqkusd6c8a">
        <div className="px-6 pt-6 pb-4 border-b border-[#E2E8F0] flex items-start justify-between flex-shrink-0" data-id="element-3651" data-mp="1e4z587ily3cv">
          <h2 id="report-settings-title" className="text-[20px] font-semibold text-[#0F172A] leading-tight" data-id="element-3652" data-mp="z8hvfkq2gwsf">
            Monthly Club Report settings
          </h2>
          <button type="button" onClick={onClose} className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" aria-label="Close" data-id="element-3653" data-mp="148w3v3vky8qz">
            <X className="w-5 h-5" data-id="element-3654" data-mp="3oyv8b3df1zou" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-3655" data-mp="8zalb4ct3ng3">
          {/* Sending */}
          <div data-id="element-3656" data-mp="47sp0tlqab1g">
            <div className="flex items-start justify-between gap-4" data-id="element-3657" data-mp="47aqtrfi9mqw">
              <div data-id="element-3658" data-mp="3vediejz99v1n">
                <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-3659" data-mp="3vdtyixq1owkz">
                  Send this report each month
                </div>
                <p className="text-[13px] text-[#64748B] mt-1" data-id="element-3660" data-mp="3qdfrnj06jsoj">
                  Sends on the 1st of each month, covering the month before.
                </p>
              </div>
              <Switch checked={draft.enabled} label="Send this report each month" onChange={enabled => setDraft(prev => ({
              ...prev,
              enabled
            }))} data-id="element-3661" data-mp="mzd2eodaicua" />
            </div>
            <div className="mt-4" data-id="element-3662" data-mp="d0497xq9128z">
              <label htmlFor="report-send-time" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3663" data-mp="32py8ucyw84xp">
                Send time
              </label>
              <div className="relative w-40" data-id="element-3664" data-mp="2xpblyxhlzflu">
                <select id="report-send-time" value={draft.sendTime} onChange={e => setDraft(prev => ({
                ...prev,
                sendTime: e.target.value
              }))} className="w-full h-[40px] pl-3 pr-9 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none" data-id="element-3665" data-mp="1d942yhqzcz2l">
                  {reportSendTimes.map(time => <option key={time} value={time} data-id="element-3666" data-mp="1wxl96n0dugev">
                      {time}
                    </option>)}
                </select>
                <ChevronDown className="w-4 h-4 text-[#475569] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-3667" data-mp="20wlmxr406sv0" />
              </div>
              <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-3668" data-mp="2ax4el81t7dnh">
                In your store's timezone
              </p>
            </div>
          </div>

          <div className="h-px bg-[#E2E8F0]" data-id="element-3669" data-mp="blbfn5vgny84" />

          {/* Recipients */}
          <div data-id="element-3670" data-mp="gjcwdk5ezlgf">
            <label htmlFor="report-recipient" className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-3671" data-mp="gitnesxqs7ej">
              Recipients
            </label>
            {draft.recipients.length > 0 && <div className="flex flex-wrap gap-2 mb-2" data-id="element-3672" data-mp="4hv44knymupu">
                {draft.recipients.map(email => <Tag key={email} label={email} color="blue" onRemove={() => setDraft(prev => ({
              ...prev,
              recipients: prev.recipients.filter(r => r !== email)
            }))} data-id="element-3673" data-mp="iguq1kk38vib" />)}
              </div>}
            <input id="report-recipient" type="email" value={newRecipient} onChange={e => {
            setNewRecipient(e.target.value);
            setRecipientError('');
          }} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addRecipient();
            }
          }} onBlur={addRecipient} placeholder="Add an email and press Enter" className={`w-full h-[40px] px-3 border rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent ${recipientError ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-3674" data-mp="ren7au9ded8c" />
            {recipientError ? <p className="text-[12px] text-[#EF4444] mt-1.5" data-id="element-3675" data-mp="1tshn1cdh5kpk">{recipientError}</p> : draft.recipients.length === 0 && <p className="text-[12px] text-[#94A3B8] mt-1.5" data-id="element-3676" data-mp="1ft04heqrv3on">
                  Add at least one recipient to receive the report.
                </p>}
          </div>

          <div className="h-px bg-[#E2E8F0]" data-id="element-3677" data-mp="162dsrvm6exb8" />

          {/* Sections */}
          <div data-id="element-3678" data-mp="1atwzfuaskxgo">
            <div className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-2" data-id="element-3679" data-mp="1auez70wnxttc">
              Sections
            </div>
            <ul className="divide-y divide-[#E2E8F0]" data-id="element-3680" data-mp="x4l9kgdc35ik">
              <li className="py-3 flex items-start justify-between gap-4" data-id="element-3681" data-mp="12122roze4a2i">
                <div data-id="element-3682" data-mp="n4uhsmg5r4m6">
                  <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-3683" data-mp="n4ayp2erua6k">Headline numbers</div>
                  <p className="text-[13px] text-[#64748B] mt-0.5" data-id="element-3684" data-mp="s3xd3f6c5wgy">
                    Members, sign-ups, cancellations and revenue
                  </p>
                  <p className="text-[12px] text-[#94A3B8] mt-1 flex items-center gap-1" data-id="element-3685" data-mp="17jcgxbtf9gvo">
                    <Lock className="w-3 h-3" data-id="element-3686" data-mp="13hz94w6rr3m4" />
                    Always included
                  </p>
                </div>
                <Switch checked label="Headline numbers" disabled data-id="element-3687" data-mp="tlw6lp8092yn" />
              </li>
              {optionalReportSections.map(section => <li key={section.key} className="py-3 flex items-start justify-between gap-4" data-id="element-3688" data-mp="on2t3wspxbyh">
                  <div data-id="element-3689" data-mp="jvujp0re1qlb">
                    <div className="text-[14px] font-medium text-[#0F172A]" data-id="element-3690" data-mp="jvb4hrt4hgaz">{section.name}</div>
                    <p className="text-[13px] text-[#64748B] mt-0.5" data-id="element-3691" data-mp="fvaq2760x53x">{section.description}</p>
                  </div>
                  <Switch checked={draft.sections[section.key]} label={section.name} onChange={on => toggleSection(section.key, on)} data-id="element-3692" data-mp="ax967k0rx1re" />
                </li>)}
            </ul>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E2E8F0] flex justify-end gap-3 flex-shrink-0" data-id="element-3693" data-mp="y0dsvlk3v75">
          <Button variant="outline" size="md" onClick={onClose} data-id="element-3694" data-mp="3myfxntcoxqh0">
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSave} data-id="element-3695" data-mp="3rvxz6yodp3ez">
            Save
          </Button>
        </div>
      </aside>
    </div>;
}